import LogoWithTextUrl from '@assets/logo-with-text.svg';
import styled from '@emotion/styled';
import { MaxMobileWindowWidth, RacemapColors } from '@racemap/utilities/consts/common';
import { Button, Flex, Menu } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOnClickOutside, useWindowSize } from 'usehooks-ts';
import { IconBars } from '../BasicComponents/Icons';
import { GlobalSearch } from '../GlobalSearch';
import { CreateMapButton } from '../TrackingMapsList/CreateMapButton';
import { UserLinks } from './UserLinks';
import { useMainNavItems } from './useMainNavItems';
import { useUserMenuItems } from './useUserMenuItems';

const CollapseBreakpoint = MaxMobileWindowWidth - 1;

export function MainHeader() {
  const headerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [menuExpanded, setMenuExpanded] = useState(false);
  const { width: windowWidth } = useWindowSize();
  const isCollapsed = windowWidth <= CollapseBreakpoint && windowWidth > 0;
  const navItems = useMainNavItems(!isCollapsed);
  const {
    groupItems: userGroupItems,
    dropdownItems: userItems,
    costCalcModal,
  } = useUserMenuItems();
  const mobileItems = userGroupItems.length > 0 ? [...navItems, ...userGroupItems] : navItems;

  // Close the navbar when clicking outside
  useOnClickOutside(headerRef, () => setMenuExpanded(false));

  // Close the navbar when the pathname changes
  useEffect(() => {
    setMenuExpanded(false);
  }, [location.pathname]);

  if (isCollapsed) {
    return (
      <Container ref={headerRef}>
        <TopBar>
          <Link to="/admin/events">
            <img src={LogoWithTextUrl} alt="» Racemap" />
          </Link>
          <Flex align="center" gap={5} style={{ marginLeft: 'auto' }}>
            <GlobalSearch compact />
            <CreateMapButton compact buttonType="ghost" buttonSize="middle" />
            <Button
              aria-label="Toggle menu"
              icon={<IconBars />}
              type="primary"
              ghost
              size="middle"
              onClick={() => setMenuExpanded((e) => !e)}
            />
          </Flex>
        </TopBar>
        {menuExpanded && (
          <>
            <MobileMenu mode="inline" items={mobileItems} />
            {costCalcModal}
          </>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <TopBar>
        <Link to="/admin/events">
          <img src={LogoWithTextUrl} alt="» Racemap" />
        </Link>
        <DesktopMenu
          disabledOverflow
          mode="horizontal"
          items={navItems}
          selectedKeys={[location.pathname]}
          triggerSubMenuAction="click"
        />
        <GlobalSearch />
        <CreateMapButton buttonSize="middle" buttonType="ghost" />
        <UserLinksWrapper>
          <UserLinks
            isCollapsed={false}
            items={userItems}
            collapsedItems={userGroupItems}
            costCalcModal={costCalcModal}
          />
        </UserLinksWrapper>
      </TopBar>
    </Container>
  );
}

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${RacemapColors.CloudBlue};
  border-bottom: 1px solid ${RacemapColors.LightGray};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  padding: 0 15px;
  gap: 8px;

  /* Scoped to the logo: the search panel renders inside this bar and its event logos must
     keep their own size. */
  > a img {
    height: 50px;
  }

  /* Leaves the search field as the only element that can give up width. Desktop only: pinning
     the ~150px logo on a phone pushes the button group off the screen. */
  @media (min-width: ${CollapseBreakpoint + 1}px) {
    > a,
    > button {
      flex: none;
    }
  }
`;

const MobileMenu = styled(Menu)`
  max-height: calc(100vh - 50px);
  overflow-y: auto;
`;

const DesktopMenu = styled(Menu)`
  border: none;
  background: transparent;
  flex: none;
`;

const UserLinksWrapper = styled.div`
  flex: none;
  margin-left: auto;
`;
